// src/pages/ia-paca/barometre-salaires.tsx
//
// Landing baromètre salaires IA PACA 2026 — lead magnet principal du
// hub IA PACA. Format : preview du contenu visible directement + form
// inline pour recevoir le PDF imprimable + MAJ trimestrielles.
//
// Stratégie : preview généreux = SEO + trust + conversion.

import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/layout';
import IaSalaryTable from '../../components/ia-paca/IaSalaryTable';
import { supabase } from '../../lib/supabase';
import {
  ArrowLeft,
  CheckCircle2,
  Mail,
  FileText,
  Calendar,
  TrendingUp,
  Building2,
  AlertCircle,
  Download,
  Sparkles,
} from 'lucide-react';

export default function BarometreSalairesIaPacaPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Report',
    name: 'Baromètre salaires IA en Provence-Alpes-Côte d\'Azur 2026',
    description:
      "Baromètre 2026 des salaires des métiers de l'IA en PACA. 13 métiers, 4 villes (Marseille, Aix, Sophia Antipolis, Nice), 4 niveaux de séniorité. Mis à jour trimestriellement.",
    datePublished: '2026-03-15',
    inLanguage: 'fr-FR',
    publisher: {
      '@type': 'Organization',
      name: 'Recruteurs.io',
      url: 'https://recruteurs.io',
    },
    about: {
      '@type': 'Thing',
      name: 'Salaires métiers IA Provence-Alpes-Côte d\'Azur',
    },
    url: 'https://recruteurs.io/ia-paca/barometre-salaires-ia-paca-2026',
  };

  return (
    <Layout
      title="Baromètre salaires IA en PACA 2026 — 13 métiers, 4 villes | recruteurs.io"
      description="Baromètre 2026 des salaires IA en Provence-Alpes-Côte d'Azur. 13 métiers, 4 villes, 4 séniorités. Recevez le PDF imprimable + mises à jour trimestrielles."
      keywords="baromètre salaires IA PACA, salaire AI Engineer PACA 2026, salaire ML Engineer Marseille, salaire MLOps Sophia, salaire Data Scientist Nice, étude salaires IA Provence"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'Baromètre salaires 2026', href: '/ia-paca/barometre-salaires-ia-paca-2026' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-violet-50/40 py-14 lg:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-20 -right-20 h-80 w-80 rounded-full bg-pink-300/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-6xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Retour au hub IA PACA
          </Link>

          <div className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-violet-700 backdrop-blur">
            <FileText className="h-3 w-3" />
            Étude exclusive · mars 2026
          </div>

          <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <h1 className="text-3xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                Baromètre des{' '}
                <span className="bg-gradient-to-r from-pink-600 via-orange-500 to-violet-600 bg-clip-text text-transparent">
                  salaires IA en PACA
                </span>{' '}
                — édition 2026.
              </h1>
              <p className="mt-5 text-lg leading-relaxed text-slate-600">
                <strong>13 métiers IA · 4 villes · 4 niveaux de séniorité.</strong>{' '}
                La référence des fourchettes salariales pratiquées en
                Provence-Alpes-Côte d'Azur. Données compilées à partir
                d'offres réelles, négociations terrain, et entretiens avec
                des candidats sur 6 mois. Mis à jour chaque trimestre.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-4 w-4" />
                  150+ entreprises observées
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <TrendingUp className="h-4 w-4" />
                  Mis à jour trimestriellement
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  Prochaine MAJ : juillet 2026
                </span>
              </div>
            </div>

            {/* Formulaire inline */}
            <div className="lg:col-span-2">
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* Preview tableau salaires global */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Tableau de référence
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            13 métiers IA en PACA — fourchettes mars 2026.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Fourchettes <strong>brut annuel hors variable</strong>, calibrées
            sur les pratiques régionales. Région PACA : ajustement -10 à
            -20% vs Paris, +5% sur Sophia Antipolis (tension forte).
          </p>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
        </div>
      </section>

      {/* Méthodologie */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Méthodologie
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Comment on construit ces fourchettes.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Pas un sondage en ligne sur LinkedIn. Pas un copier-coller de
            Glassdoor. Une triangulation entre 4 sources qui se vérifient
            mutuellement.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            <MethodCard
              num="01"
              title="Offres d'emploi PACA"
              body="Veille systématique sur Hellowork, Welcome to the Jungle, LinkedIn, Indeed et APEC, filtrée IA / Data / ML, sur les 4 villes du périmètre. 800+ annonces analysées sur 12 mois roulants."
            />
            <MethodCard
              num="02"
              title="Entretiens candidats"
              body="Données de négociation collectées en pré-qualification (salaire actuel, salaire visé, contre-offre obtenue). 250+ candidats sur 12 mois, anonymisés et agrégés par séniorité."
            />
            <MethodCard
              num="03"
              title="Briefs clients"
              body="Budgets que les entreprises PACA mettent réellement sur la table (vs ce qu'elles affichent en annonce). Donne la fourchette haute praticable, souvent non publiée."
            />
            <MethodCard
              num="04"
              title="Sources tierces"
              body="Confrontation avec Glassdoor, Levels.fyi, Hellowork barometer, JeDha, Factoriel pour calibrer. Quand les écarts sont importants, on tranche en faveur des données primaires (sources 1, 2, 3)."
            />
          </div>
        </div>
      </section>

      {/* Ce que vous obtenez */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Contenu du baromètre
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Ce que vous recevez par email.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              Icon={FileText}
              title="PDF imprimable"
              body="22 pages mises en page pour impression A4 / présentation comité direction. Tableaux salaires détaillés par ville et par métier, charts d'évolution."
            />
            <FeatureCard
              Icon={Building2}
              title="Vue par ville"
              body="Tableaux dédiés Marseille / Aix / Sophia Antipolis / Nice. Permet de calibrer une offre selon le bassin réel et non un moyennage régional trompeur."
            />
            <FeatureCard
              Icon={TrendingUp}
              title="Tendances 12 mois"
              body="Évolution des fourchettes vs 2025, métiers en tension, métiers en stabilisation. Pour anticiper vos prochains recrutements."
            />
            <FeatureCard
              Icon={Sparkles}
              title="Métiers émergents"
              body="Focus sur Agentic AI Engineer, AI Automation Specialist, Architecte IA — métiers qui n'existaient pas en 2024 et qui pèsent déjà sur les budgets."
            />
            <FeatureCard
              Icon={CheckCircle2}
              title="Guide d'utilisation"
              body="Comment calibrer une fourchette pour une annonce (junior vs confirmé vs senior), comment négocier face à une contre-offre, ce qu'il faut ajouter au salaire fixe."
            />
            <FeatureCard
              Icon={Mail}
              title="Mises à jour trimestrielles"
              body="Chaque trimestre, nous vous envoyons l'édition révisée. Pas plus, pas moins. Vous pouvez vous désinscrire en 1 clic à tout moment."
            />
          </div>
        </div>
      </section>

      {/* FAQ courte */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Questions fréquentes
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Vos questions, nos réponses.
          </h2>
          <div className="mt-8 space-y-3">
            {FAQ.map((q) => (
              <details
                key={q.question}
                className="group rounded-2xl border border-slate-200 bg-white p-5"
              >
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-base font-semibold text-slate-900 [&::-webkit-details-marker]:hidden">
                  <span>{q.question}</span>
                  <span className="mt-0.5 text-slate-400 transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <div className="mt-4 text-sm leading-relaxed text-slate-600">
                  {q.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-white py-14">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Prêt à recevoir le baromètre 2026 ?
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Remplissez le formulaire en haut de la page. Le baromètre arrive
            dans votre boîte mail sous 2 minutes.
          </p>
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-pink-200/50 transition hover:shadow-xl"
          >
            <Download className="h-4 w-4" />
            Recevoir le baromètre par email
          </a>
        </div>
      </section>
    </Layout>
  );
}

function LeadForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const cleanFirst = firstName.trim();
    const cleanLast = lastName.trim();
    const cleanEmail = email.trim().toLowerCase();
    const cleanCompany = company.trim();

    if (!cleanFirst || !cleanLast || !cleanEmail) {
      setError('Veuillez remplir prénom, nom et email.');
      setSubmitting(false);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('Adresse email invalide.');
      setSubmitting(false);
      return;
    }

    try {
      const { error: insertError } = await supabase.from('leads').insert([
        {
          first_name: cleanFirst,
          last_name: cleanLast,
          email: cleanEmail,
          company: cleanCompany,
          source: 'barometre_salaires_ia_paca_2026',
        },
      ]);

      if (insertError) {
        console.error('[barometre] insert lead error:', insertError.message);
        setError(
          "Impossible d'enregistrer votre demande. Réessayez ou contactez contact@recruteurs.io.",
        );
        setSubmitting(false);
        return;
      }

      // Notification email best-effort
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

      if (supabaseUrl && supabaseAnonKey) {
        fetch(`${supabaseUrl.replace(/\/$/, '')}/functions/v1/whitepaper-notify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabaseAnonKey}`,
            apikey: supabaseAnonKey,
          },
          body: JSON.stringify({
            paper: {
              slug: 'barometre-salaires-ia-paca-2026',
              title: 'Baromètre salaires IA PACA 2026',
            },
            lead: {
              firstName: cleanFirst,
              lastName: cleanLast,
              email: cleanEmail,
              company: cleanCompany,
            },
            metadata: {
              submittedAt: new Date().toISOString(),
              userAgent:
                typeof navigator !== 'undefined' ? navigator.userAgent : '',
              referrer:
                typeof window !== 'undefined' ? window.location.pathname : '',
            },
          }),
          keepalive: true,
        }).catch((err) => {
          console.warn('[barometre] notify email failed:', err);
        });
      }

      setSubmitted(true);
    } catch (err) {
      console.error('[barometre] submit error:', err);
      setError('Une erreur est survenue. Réessayez ou contactez-nous.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 shadow-lg">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
          <div>
            <h3 className="text-base font-bold text-emerald-900">
              Merci, votre demande est enregistrée.
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-emerald-800">
              Le baromètre va vous être envoyé à <strong>{email}</strong> sous
              2 minutes. Pensez à vérifier vos spams (et marquez-nous comme
              "non spam" pour recevoir les MAJ trimestrielles).
            </p>
            <p className="mt-3 text-sm text-emerald-800">
              En attendant, n'hésitez pas à parcourir le{' '}
              <Link
                to="/ia-paca"
                className="font-semibold text-emerald-900 underline"
              >
                hub IA PACA
              </Link>{' '}
              pour des analyses complémentaires.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border-2 border-violet-200 bg-white p-6 shadow-lg"
    >
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-violet-600" />
        <h3 className="text-base font-bold text-slate-900">
          Recevoir le baromètre 2026
        </h3>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        PDF imprimable envoyé par email. Sans engagement, désinscription 1
        clic.
      </p>

      <div className="mt-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Prénom"
            required
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
          />
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Nom"
            required
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
          />
        </div>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email professionnel"
          required
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
        />
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Entreprise (optionnel)"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
        />
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 px-4 py-3 text-sm font-semibold text-white shadow-md shadow-pink-200/50 transition hover:shadow-lg disabled:opacity-60"
      >
        {submitting ? (
          'Envoi en cours...'
        ) : (
          <>
            <Download className="h-4 w-4" />
            Recevoir le baromètre
          </>
        )}
      </button>

      <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
        En remplissant ce formulaire, vous acceptez de recevoir le baromètre
        ainsi que ses mises à jour trimestrielles. Vos données ne sont
        jamais partagées avec un tiers. Conformité RGPD.
      </p>
    </form>
  );
}

function MethodCard({
  num,
  title,
  body,
}: {
  num: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="text-[11px] font-bold uppercase tracking-wider text-violet-600">
        {num}
      </div>
      <h3 className="mt-1 text-base font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

function FeatureCard({
  Icon,
  title,
  body,
}: {
  Icon: typeof FileText;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-violet-300 hover:shadow-md">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-3 text-base font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

const FAQ = [
  {
    question: "Le baromètre est-il vraiment gratuit ?",
    answer:
      "Oui. Pas de carte bancaire, pas de période d'essai déguisée, pas de vente forcée. On collecte votre email pour vous envoyer le PDF et les MAJ trimestrielles. Vous pouvez vous désinscrire en 1 clic à tout moment.",
  },
  {
    question: "À quelle fréquence le baromètre est-il mis à jour ?",
    answer:
      "Quatre fois par an : mars, juillet, septembre, décembre. Le marché IA évolue vite (nouveaux métiers, tensions salariales, recompositions). Une donnée annuelle serait obsolète à mi-année.",
  },
  {
    question: "Vos fourchettes incluent-elles le variable ?",
    answer:
      "Non. Les fourchettes affichées sont en brut annuel fixe hors variable, hors BSPCE/AGA, hors prime de participation. Le PDF complet précise les ordres de grandeur des composantes variables par typologie d'entreprise (corporate, scale-up, startup early).",
  },
  {
    question: "Mes données sont-elles partagées avec des tiers ?",
    answer:
      "Non. Vos données restent chez Recruteurs.io et sont utilisées uniquement pour vous envoyer le baromètre et ses MAJ. Hébergement Supabase (UE), conformité RGPD. Désinscription en 1 clic via tout email reçu.",
  },
  {
    question: "Puis-je utiliser ce baromètre dans une présentation interne ?",
    answer:
      "Oui, tant que vous citez la source (\"Baromètre Recruteurs.io 2026\") et que vous ne le republiez pas tel quel sur internet. Pour une diffusion publique ou commerciale, contactez-nous à contact@recruteurs.io.",
  },
];
